import { fetchJSON as fetchAPI } from './client';

export type PlanningRecommendation = {
  id: string;
  tenant_id: string;
  recommendation_id: string;
  source_module: string;
  agent_type: string;
  type: string;
  priority: string;
  title: string;
  description: string;
  reasoning_summary: string;
  business_rationale: string;
  confidence: number;
  evidence: { source: string; description: string; confidence: number; ref_id?: string }[];
  data_sources: string[];
  assumptions: string[];
  alternative_options: string[];
  tradeoffs?: string;
  expected_revenue_impact: number;
  expected_margin_impact_pct: number;
  working_capital_impact: number;
  inventory_impact?: string;
  supply_impact?: string;
  operational_impact?: string;
  risk_assessment: string;
  risk_score: number;
  approval_path: string;
  approval_status: string;
  execution_status: string;
  expected_outcome: string;
  known_risks: string[];
  auto_execute: boolean;
  created_at: string;
  updated_at: string;
};

export type PlanningDecision = {
  id: string;
  tenant_id: string;
  title: string;
  description: string;
  priority: string;
  status: string;
  recommendation_ids: string[];
  decision_owner: string;
  approval_path: string;
  financial_impact: number;
  created_at: string;
  updated_at: string;
};

export type DecisionGraphNode = {
  id: string;
  label: string;
  node_type: string;
  module: string;
  confidence: number;
  dependencies: string[];
};

export type DecisionGraph = {
  tenant_id: string;
  nodes: DecisionGraphNode[];
  edges: { from: string; to: string; relation: string }[];
};

export type LearningRecord = {
  id: string;
  tenant_id: string;
  recommendation_id: string;
  feedback_type: string;
  approval_pattern?: string;
  rejection_reason?: string;
  execution_result?: string;
  business_outcome?: string;
  outcome_score: number;
  planner_feedback?: string;
  executive_feedback?: string;
  model_improvement?: string;
  created_at: string;
};

export type DecisionDashboard = {
  tenant_id: string;
  total_recommendations: number;
  pending_review: number;
  pending_approval: number;
  decision_acceptance_rate: number;
  recommendation_accuracy: number;
  business_outcome_score: number;
  decision_cycle_time_hours: number;
  approval_efficiency: number;
  execution_success_rate: number;
  learning_improvement: number;
  decision_quality_index: number;
  recommendation_roi: number;
  enterprise_decision_health: number;
  active_agents: number;
};

export type PlanningAgent = {
  type: string;
  name: string;
  responsibilities: string[];
  inputs: string[];
  outputs: string[];
  confidence: number;
  escalation_rules?: string;
};

const API_BASE = import.meta.env.VITE_PLANNINGDECISIONINTEL_API ?? '/api/v1';

function pdiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const planningDecisionIntelApi = {
  dashboard: () => pdiFetch<DecisionDashboard>('/decision-dashboard'),
  listDecisions: () => pdiFetch<PlanningDecision[]>('/decisions'),
  listRecommendations: (status?: string) =>
    pdiFetch<PlanningRecommendation[]>(`/recommendations${status ? `?status=${status}` : ''}`),
  reviewRecommendation: (id: string, comment?: string) =>
    pdiFetch<PlanningRecommendation>(`/recommendations/${id}/review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment }),
    }),
  approveRecommendation: (id: string) =>
    pdiFetch<PlanningRecommendation>(`/recommendations/${id}/approve`, { method: 'POST' }),
  rejectRecommendation: (id: string, reason: string) =>
    pdiFetch<PlanningRecommendation>(`/recommendations/${id}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }),
  executeRecommendation: (id: string) =>
    pdiFetch<PlanningRecommendation>(`/recommendations/${id}/execute`, { method: 'POST' }),
  decisionHistory: () => pdiFetch<PlanningRecommendation[]>('/decision-history'),
  learning: () => pdiFetch<LearningRecord[]>('/learning'),
  decisionGraph: () => pdiFetch<DecisionGraph>('/decision-graph'),
  listAgents: () => pdiFetch<PlanningAgent[]>('/planning-decision/agents'),
};
